import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PersonaCopy } from '../data/personaCopy';
import { useForm } from 'react-hook-form';
import { FormValidation, DefaultBias } from '../utils/behavioralScience';
import { CheckCircle, AlertCircle, ArrowLeft, Search, Clock, Brain, ChevronDown } from 'lucide-react';
import { 
  WATCH_LATER_SIZES, 
  PREDEFINED_PLATFORMS, 
  PREDEFINED_USE_CASES, 
  PREDEFINED_ROLES,
  WatchLaterSize 
} from '../types/waitlist';
import { waitlistAPI } from '../services/waitlistApi';
import VerificationPendingBanner from './VerificationPendingBanner';

interface HeroSectionProps {
  copy: PersonaCopy;
  onSubmit: (email: string, keepUpdated: boolean, questionsData?: any) => Promise<void>;
  isLoading: boolean;
  activeTab: 'waitlist' | 'mission';
  onTermsClick: () => void;
}

interface FormData {
  email: string;
  keepUpdated: boolean;
  referralCode?: string;
}

interface QuestionsFormData {
  whatWouldYouQuellect?: string;
  nextPlatforms: string[];
  primaryUseCase?: string;
  role?: string;
  watchLaterSize?: WatchLaterSize;
  otherThoughts?: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({ copy, onSubmit, isLoading, activeTab, onTermsClick }) => {
  const [currentStep, setCurrentStep] = useState<'email' | 'questions'>('email');
  const [emailData, setEmailData] = useState<FormData | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [otherPlatform, setOtherPlatform] = useState('');
  const [otherUseCase, setOtherUseCase] = useState('');
  const [otherRole, setOtherRole] = useState('');
  const [apiError, setApiError] = useState<string | null>(null);
  const [showVerificationBanner, setShowVerificationBanner] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      email: '',
      keepUpdated: DefaultBias.getDefaultKeepUpdated(),
      referralCode: '',
    },
  });

  const {
    register: registerQuestions,
    handleSubmit: handleSubmitQuestions,
    watch: watchQuestions,
    setValue: setQuestionsValue,
    formState: { errors: questionErrors },
  } = useForm<QuestionsFormData>({
    defaultValues: {
      nextPlatforms: [],
      // role: DefaultBias.getDefaultRole(),
      // watchLaterSize: DefaultBias.getDefaultWatchLaterSize() as WatchLaterSize,
    },
  });

  const watchedEmail = watch('email');
  const watchedKeepUpdated = watch('keepUpdated');
  const watchedReferralCode = watch('referralCode');
  const watchedQuestions = watchQuestions();

  // Detect referral code from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const referralCode = urlParams.get('referral');
    
    if (referralCode && !watchedReferralCode) {
      const normalizedCode = referralCode.toUpperCase();
      setValue('referralCode', normalizedCode);
      
      // Track referral visit (analytics)
      console.log('Referral code detected from URL:', normalizedCode);
    }
  }, [setValue, watchedReferralCode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.custom-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Questions configuration with proper validation constants
  const questions = [
      {
        id: 'watchLaterSize',
        label: 'How many items are in your "Save for Later" list? (Across YouTube, Instagram, TikTok, etc.)',
        type: 'radio',
        options: WATCH_LATER_SIZES,
        helpText: ''
      },
    {
      id: 'whatWouldYouQuellect',
      label: 'What would you like to Quellect?',
      type: 'textarea',
      placeholder: 'e.g., AI tutorials, cooking videos, business podcasts...',
      helpText: 'Tell us about the content you want to collect'
    },
    {
      id: 'nextPlatforms',
      label: 'Which platforms would you like us to support next?',
      type: 'checkbox',
      options: [...PREDEFINED_PLATFORMS, 'Other'],
      helpText: 'Select all that apply'
    },
    {
      id: 'primaryUseCase',
      label: 'What\'s your primary use case?',
      type: 'radio',
      options: [...PREDEFINED_USE_CASES, 'Other'],
      helpText: 'Choose the most relevant option'
    },
    {
      id: 'role',
      label: 'What\'s your role?',
      type: 'select',
      options: [...PREDEFINED_ROLES, 'Other'],
      helpText: 'This helps us personalize your experience'
    },
    {
      id: 'otherThoughts',
      label: 'Anything else you want to share?',
      type: 'textarea',
      placeholder: 'Yes, I was thinking...',
      helpText: 'Share any additional thoughts, feedback, or ideas...'
    }
  ];

  const totalQuestions = questions.length;
  const questionsProgress = (currentQuestionIndex / totalQuestions) * 100;

  React.useEffect(() => {
    if (hasAttemptedSubmit && watchedEmail) {
      const error = FormValidation.getEmailError(watchedEmail);
      setEmailError(error);
    } else if (hasAttemptedSubmit && !watchedEmail) {
      setEmailError('Email is required');
    } else {
      setEmailError(null);
    }
  }, [watchedEmail, hasAttemptedSubmit]);

  // Navigation functions
  const goToNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const canGoNext = () => {
    const currentQuestion = questions[currentQuestionIndex];
    
    // Question 1: watchLaterSize (radio)
    if (currentQuestion.id === 'watchLaterSize') {
      const result = watchedQuestions.watchLaterSize;
      return result;
    }
    
    // Question 2: whatWouldYouQuellect (textarea)
    if (currentQuestion.id === 'whatWouldYouQuellect') {
      const result = watchedQuestions.whatWouldYouQuellect?.trim();
      return result;
    }
    
    // Question 3: nextPlatforms (checkbox)
    if (currentQuestion.id === 'nextPlatforms') {
      const hasSelection = watchedQuestions.nextPlatforms?.length > 0;
      
      // If "Other" is selected, ensure the input field is filled
      if (hasSelection && watchedQuestions.nextPlatforms?.includes('Other')) {
        const result = otherPlatform.trim().length > 0;
        return result;
      }
      return hasSelection;
    }
    
    // Question 4: primaryUseCase (radio)
    if (currentQuestion.id === 'primaryUseCase') {
      const hasSelection = watchedQuestions.primaryUseCase;
      
      // If "Other" is selected, ensure the input field is filled
      if (hasSelection === 'Other') {
        const result = otherUseCase.trim().length > 0;
        return result;
      }
      return hasSelection;
    }
    
    // Question 5: role (select)
    if (currentQuestion.id === 'role') {
      const hasSelection = watchedQuestions.role;
      
      // If "Other" is selected, ensure the input field is filled
      if (hasSelection === 'Other') {
        const result = otherRole.trim().length > 0;
        return result;
      }
      return hasSelection;
    }
    
    // Question 6: otherThoughts (textarea) - required
    if (currentQuestion.id === 'otherThoughts') {
      return true;
    }
    return false;
  };

  const handleMultiSelectChange = (value: string, checked: boolean) => {
    const currentValues = watchedQuestions.nextPlatforms || [];
    if (checked) {
      setQuestionsValue('nextPlatforms', [...currentValues, value]);
    } else {
      setQuestionsValue('nextPlatforms', currentValues.filter(v => v !== value));
    }
  };

  const handleResendVerification = async (email: string) => {
    try {
      // This would call the resend verification API endpoint
      // For now, we'll just show a success message
      console.log('Resending verification email to:', email);
      // In a real implementation, you would call:
      // await waitlistAPI.resendVerification(email);
    } catch (error) {
      console.error('Failed to resend verification email:', error);
      throw error;
    }
  };

  const onEmailSubmit = async (data: FormData) => {
    setHasAttemptedSubmit(true);
    setApiError(null);
    
    if (isLoading) return;
    
    try {
      // Submit email to FastAPI backend
      const response = await waitlistAPI.submitEmail(data.email, data.keepUpdated, data.referralCode);
      
      if (response.success) {
        setEmailData(data);
        setEmailSubmitted(true);
        
        // Show verification banner if verification is required
        if (response.data?.verificationRequired) {
          setVerificationEmail(data.email);
          setShowVerificationBanner(true);
        }
        
        // Automatically transition to questions immediately
        setCurrentStep('questions');
      } else {
        setApiError(response.message || 'Email submission failed');
      }
    } catch (error) {
      console.error('Email submission failed:', error);
      setApiError(error instanceof Error ? error.message : 'Email submission failed');
    }
  };

  const onQuestionsSubmit = async (data: QuestionsFormData) => {
    console.log('onQuestionsSubmit called with data:', data);
    if (isLoading || !emailData) return;
    
    setApiError(null);
    
    try {
      // If "Other" is selected, replace it with the actual input value
      let processedData = { ...data };
      
      // Handle nextPlatforms "Other"
      if (data.nextPlatforms?.includes('Other') && otherPlatform.trim()) {
        processedData.nextPlatforms = data.nextPlatforms
          .filter(platform => platform !== 'Other')
          .concat(otherPlatform.trim());
      }
      
      // Handle primaryUseCase "Other"
      if (data.primaryUseCase === 'Other' && otherUseCase.trim()) {
        processedData.primaryUseCase = otherUseCase.trim();
      }
      
      // Handle role "Other"
      if (data.role === 'Other' && otherRole.trim()) {
        processedData.role = otherRole.trim();
      }
      
      // Validate required fields
      if (!processedData.watchLaterSize || !processedData.whatWouldYouQuellect || 
          !processedData.nextPlatforms || !processedData.primaryUseCase || !processedData.role) {
        setApiError('Please fill in all required fields');
        return;
      }
      
      // Submit questions to FastAPI backend
      const response = await waitlistAPI.submitQuestions({
        watchLaterSize: processedData.watchLaterSize,
        whatWouldYouQuellect: processedData.whatWouldYouQuellect,
        nextPlatforms: processedData.nextPlatforms,
        primaryUseCase: processedData.primaryUseCase,
        role: processedData.role,
        otherThoughts: processedData.otherThoughts || ''
      });
      
      if (response.success && response.data) {
        console.log('Processed data:', processedData);
        // Pass only the referral link from the response
        const submissionData = {
          ...processedData,
          referralData: {
            referralLink: response.data.referralLink
          }
        };
        await onSubmit(emailData.email, emailData.keepUpdated, submissionData);
      } else {
        setApiError(response.message || 'Questions submission failed');
      }
    } catch (error) {
      console.error('Questions submission failed:', error);
      setApiError(error instanceof Error ? error.message : 'Questions submission failed');
    }
  };

  const goBackToEmail = () => {
    setCurrentStep('email');
    setEmailData(null);
    setEmailSubmitted(false);
    setHasAttemptedSubmit(false);
    setCurrentQuestionIndex(0);
    setOtherPlatform('');
    setOtherUseCase('');
    setOtherRole('');
    setApiError(null);
    setShowVerificationBanner(false);
    setVerificationEmail('');
    // Clear session when going back
    waitlistAPI.clearSession();
  };

  const features = [
    {
      icon: Clock,
      title: "Instant",
      description: "Get answers in seconds, not hours of rewatching"
    },
    {
      icon: Search,
      title: "Searchable",
      description: "Find any concept across your entire content library"
    },
    {
      icon: Brain,
      title: "AI-Powered",
      description: "AI extracts and synthesizes key insights for you"
    }    
  ];

  // Mission content
  const missionContent = (
    <div className="text-center max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="space-y-8 text-left"
      >
        <p className="text-lg text-gray-700 leading-relaxed">
          At Quellect, we believe your saved content should work for you, not against you. While other platforms scatter your bookmarks into digital graveyards, we turn them into personalized, collaborative knowledge base. 
        </p>
        <p className="text-lg text-gray-700 leading-relaxed">
          Our AI learns your interests and how you think, making searches smarter and conversations deeper over time. Share collections with others and transform your personal vault into a living resource that grows with every interaction.
        </p>
        <p className="text-lg text-gray-700 leading-relaxed">
          We're creating more than just a bookmark manager—we're building a better way to share knowledge. Join us in reshaping how we collect and connect ideas, one saved link at a time.
        </p>
      </motion.div>
    </div>
  );

  // Waitlist content
  const waitlistContent = (
    <>
      {/* Content based on current step */}
      <AnimatePresence mode="wait">
        {currentStep === 'email' ? (
          <motion.div
            key="email"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="text-3xl sm:text-4xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight px-6"
            >
              <span className="text-gradient">Quellect</span> and search your saved content the intelligent way
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="text-xl text-gray-600 mb-8 leading-relaxed"
            >
              Stop re-scrolling and re-watching just to find that one post
            </motion.p>

            {/* Email Form */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="max-w-md mx-auto"
            >
              <form onSubmit={handleSubmit(onEmailSubmit)} className="space-y-6">
                {/* Email Input */}
                <div>
                  <div className="relative">
                    <input
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Please enter a valid email address',
                        },
                      })}
                      type="email"
                      id="email"
                      placeholder="your.email@example.com"
                      className={`form-input pr-10 ${
                        emailError || apiError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                      }`}
                      disabled={isLoading || emailSubmitted}
                    />

                    {(emailError || apiError) && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <AlertCircle className="h-5 w-5 text-red-500" />
                      </div>
                    )}
                  </div>
                  {(emailError || apiError) && (
                    <p className="mt-2 text-sm text-red-600 flex items-center text-left">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {emailError || apiError}
                    </p>
                  )}
                </div>



                {/* Keep Updated Checkbox */}
                <div className="flex items-start space-x-3">
                  <div className="flex items-center h-5">
                    <input
                      {...register('keepUpdated')}
                      id="keepUpdated"
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      disabled={isLoading || emailSubmitted}
                    />
                  </div>
                  <div className="text-sm text-left">
                    <label htmlFor="keepUpdated" className="font-medium text-gray-700 cursor-pointer">
                      Keep me updated about Quellect
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!isValid || isLoading || emailSubmitted}
                  className={`w-full btn-primary ${
                    !isValid || isLoading || emailSubmitted
                      ? 'opacity-50 cursor-not-allowed transform-none'
                      : ''
                  }`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Joining...</span>
                    </div>
                  ) : emailSubmitted ? (
                    <div className="flex items-center justify-center space-x-2">
                      <CheckCircle className="w-5 h-5" />
                      <span>Added to Waitlist!</span>
                    </div>
                  ) : (
                    'Get Early Access'
                  )}
                </button>

                {/* Verification Pending Banner */}
                <AnimatePresence>
                  {showVerificationBanner && (
                    <VerificationPendingBanner
                      email={verificationEmail}
                      onResendVerification={handleResendVerification}
                      onClose={() => setShowVerificationBanner(false)}
                    />
                  )}
                </AnimatePresence>

                {/* Terms Notice */}
                <p className="text-xs text-gray-500 text-center leading-relaxed">
                  By submitting your email, you agree to our{' '}
                  <button
                    type="button"
                    onClick={onTermsClick}
                    className="text-primary-600 hover:text-primary-700 underline font-medium"
                  >
                    Terms & Conditions
                  </button>
                </p>
              </form>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="questions"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Back Button */}
            <div className="text-left mb-6 ml-2">
              <button
                onClick={goBackToEmail}
                className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to email</span>
              </button>
            </div>

            {/* Questions Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Help us personalize your experience
              </h2>
            </div>

            {/* Progress Bar */}
            <div className="mb-8 max-w-2xl mx-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Question {currentQuestionIndex + 1} of {totalQuestions}</span>
                <span className="text-sm text-gray-500">{Math.round(questionsProgress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <motion.div
                  className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${questionsProgress}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </div>
            </div>

            {/* API Error Display */}
            {apiError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-2 text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">{apiError}</span>
                </div>
              </div>
            )}

            {/* Current Question */}
            <form onSubmit={(e) => {
              // Only allow form submission on the last question
              if (currentQuestionIndex < totalQuestions - 1) {
                e.preventDefault();
                goToNextQuestion();
              } else {
                handleSubmitQuestions(onQuestionsSubmit)(e);
              }
            }} className="max-w-lg mx-auto">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentQuestionIndex}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  {/* Question Label */}
                  <div className="text-center">
                    <label className="form-label text-lg">
                      {questions[currentQuestionIndex].id === 'watchLaterSize' ? (
                        <>
                          <div>How many items are in your "Save for Later" list?</div>
                          <div className="text-sm text-gray-600 mt-1">(YouTube, Instagram, TikTok, etc.)</div>
                        </>
                      ) : (
                        questions[currentQuestionIndex].label
                      )}
                    </label>
                    <p className="mt-2 text-sm text-gray-500">
                      {questions[currentQuestionIndex].helpText}
                    </p>
                  </div>

                  {/* Question Input */}
                  <div className="flex justify-center">
                    <div className="w-full max-w-md">
                      {questions[currentQuestionIndex].type === 'textarea' && (
                        <textarea
                          {...registerQuestions(questions[currentQuestionIndex].id as keyof QuestionsFormData)}
                          placeholder={questions[currentQuestionIndex].placeholder}
                          className="form-input min-h-[80px] resize-none"
                          disabled={isLoading}
                        />
                      )}

                  {questions[currentQuestionIndex].type === 'checkbox' && (
                      <div className="space-y-3">
                        {questions[currentQuestionIndex].options?.map((option) => (
                          <label key={option} className="flex items-center space-x-3 cursor-pointer justify-start p-2 rounded-3xl hover:bg-gray-50 transition-colors duration-200 group border border-gray-200 hover:border-primary-200">
                            <div className="relative ml-1.5">
                              <input
                                type="checkbox"
                                checked={watchedQuestions.nextPlatforms?.includes(option) || false}
                                onChange={(e) => handleMultiSelectChange(option, e.target.checked)}
                                className={`h-5 w-5 focus:ring-primary-500 border-gray-300 rounded-lg transition-all duration-200 group-hover:border-primary-400 appearance-none mt-1.5 ${
                                  watchedQuestions.nextPlatforms?.includes(option) 
                                    ? 'bg-primary-600 border-primary-600' 
                                    : 'bg-white border-gray-300'
                                }`}
                                disabled={isLoading}
                              />
                              {watchedQuestions.nextPlatforms?.includes(option) && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20" strokeWidth="5">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <span className="text-gray-700 font-medium group-hover:text-gray-900 transition-colors duration-200">{option}</span>
                          </label>
                        ))}
                      
                      {/* Other input field for nextPlatforms - only show when Other is checked */}
                      {watchedQuestions.nextPlatforms?.includes('Other') && (
                        <div className="ml-7">
                          <input
                            type="text"
                            value={otherPlatform}
                            onChange={(e) => setOtherPlatform(e.target.value)}
                            placeholder="Please specify platform..."
                            className="form-input w-full"
                            disabled={isLoading}
                            required={watchedQuestions.nextPlatforms?.includes('Other')}
                          />
                        </div>
                      )}
                      
                    </div>
                  )}

                  {questions[currentQuestionIndex].type === 'radio' && (
                    <div className="space-y-3">
                      {questions[currentQuestionIndex].options?.map((option) => (
                        <label key={option} className="flex items-center space-x-3 cursor-pointer justify-start p-2 rounded-3xl hover:bg-gray-50 transition-colors duration-200 group border border-gray-200 hover:border-primary-200">
                          <div className="relative ml-1.5">
                            <input
                              type="radio"
                              value={option}
                              {...registerQuestions(questions[currentQuestionIndex].id as keyof QuestionsFormData)}
                              className={`h-5 w-5 focus:ring-primary-500 border-gray-300 rounded-full transition-all duration-200 group-hover:border-primary-400 appearance-none mt-1.5 ${
                                (questions[currentQuestionIndex].id === 'primaryUseCase' && watchedQuestions.primaryUseCase === option) ||
                                (questions[currentQuestionIndex].id === 'watchLaterSize' && watchedQuestions.watchLaterSize === option)
                                  ? 'bg-primary-600 border-primary-600' 
                                  : 'bg-white border-gray-300'
                              }`}
                              disabled={isLoading}
                            />
                            {(questions[currentQuestionIndex].id === 'primaryUseCase' && watchedQuestions.primaryUseCase === option) ||
                             (questions[currentQuestionIndex].id === 'watchLaterSize' && watchedQuestions.watchLaterSize === option) ? (
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              </div>
                            ) : null}
                          </div>
                          <span className="text-gray-700 font-medium group-hover:text-gray-900 transition-colors duration-200">{option}</span>
                        </label>
                      ))}
                      
                      {/* Other input field for radio questions - only show when Other is checked */}
                      {questions[currentQuestionIndex].id === 'primaryUseCase' && watchedQuestions.primaryUseCase === 'Other' && (
                        <div className="ml-7">
                          <input
                            type="text"
                            value={otherUseCase}
                            onChange={(e) => setOtherUseCase(e.target.value)}
                            placeholder="Please specify use case..."
                            className="form-input w-full"
                            disabled={isLoading}
                            required={watchedQuestions.primaryUseCase === 'Other'}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {questions[currentQuestionIndex].type === 'select' && (
                    <div className="space-y-3 custom-dropdown">
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          className="form-input w-full text-left flex items-center justify-between bg-white/90 backdrop-blur-sm px-4 py-3 border border-gray-300 rounded-3xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                          disabled={isLoading}
                        >
                          <span className={watchedQuestions.role ? 'text-gray-900' : 'text-gray-500'}>
                            {watchedQuestions.role || 'Select an option'}
                          </span>
                          <ChevronDown 
                            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                              isDropdownOpen ? 'rotate-180' : ''
                            }`} 
                          />
                        </button>
                        
                                                 {/* Simple dropdown for testing */}
                         {isDropdownOpen && (
                           <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-3xl shadow-lg max-h-60 overflow-y-auto scrollbar-hide">
                            {questions[currentQuestionIndex].options?.map((option) => (
                              <button
                                key={option}
                                type="button"
                                onClick={() => {
                                  setQuestionsValue(questions[currentQuestionIndex].id as keyof QuestionsFormData, option);
                                  setIsDropdownOpen(false);
                                }}
                                className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 ${
                                  watchedQuestions.role === option ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                                }`}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Other input field for select questions - only show when Other is selected */}
                      {questions[currentQuestionIndex].id === 'role' && watchedQuestions.role === 'Other' && (
                        <div>
                          <input
                            type="text"
                            value={otherRole}
                            onChange={(e) => setOtherRole(e.target.value)}
                            placeholder="Please specify your role..."
                            className="form-input w-full"
                            disabled={isLoading}
                            required={watchedQuestions.role === 'Other'}
                          />
                        </div>
                      )}
                    </div>
                  )}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-8">
                <button
                  type="button"
                  onClick={goToPreviousQuestion}
                  disabled={currentQuestionIndex === 0 || isLoading}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    currentQuestionIndex === 0 || isLoading
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-primary-600 hover:text-primary-700'
                  }`}
                >
                  ← Previous
                </button>

                {currentQuestionIndex < totalQuestions - 1 ? (
                  <button
                    type="submit"
                    disabled={!canGoNext() || isLoading}
                    className={`px-6 py-2 rounded-3xl transition-colors ${
                      !canGoNext() || isLoading
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    Next →
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!canGoNext() || isLoading}
                    className={`px-6 py-2 rounded-3xl transition-colors ${
                      !canGoNext() || isLoading
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Submitting...</span>
                      </div>
                    ) : (
                      'Submit'
                    )}
                  </button>
                )}
              </div>

              {/* Skip Option */}
              <div className="text-center mt-6">
                <button
                  type="button"
                  onClick={() => onSubmit(emailData!.email, emailData!.keepUpdated, {})}
                  disabled={isLoading}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Skip questions for now
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  return (
    <section className="relative overflow-hidden py-20 sm:py-12">
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Main Content Card */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="card max-w-5xl mx-auto mb-16 min-h-[480px]"
          >
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="w-20 h-20 rounded-2xl mx-auto mb-4 flex items-center justify-center"
            >
              <img src="/logo.svg" alt="Quellect" className="w-16 h-16 drop-shadow-lg" />
            </motion.div>

            {/* Email Success Notification - Show on both email and questions steps */}
            <AnimatePresence>
              {emailSubmitted && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg relative"
                >
                  {/* Close Button */}
                  <button
                    onClick={() => setEmailSubmitted(false)}
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18"/>
                      <path d="m6 6 12 12"/>
                    </svg>
                  </button>
                  
                  <div className="flex flex-col items-center justify-center text-green-700">
                    <CheckCircle className="w-5 h-5 mb-2" />
                    <span className="font-medium text-center mb-1">
                      <strong>Email submitted successfully!</strong> Check your inbox to verify your email
                    </span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Content based on active tab */}
            <AnimatePresence mode="wait">
              {activeTab === 'mission' ? (
                <motion.div
                  key="mission"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {missionContent}
                </motion.div>
              ) : (
                <motion.div
                  key="waitlist"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {waitlistContent}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Features Grid - Show on all tabs */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className={`grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto ${
              currentStep === 'questions' ? 'mt-28 mb-8' : 'mb-2'
            }`}
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, delay: 0.3 + index * 0.05 }}
                className="text-center group"
              >
                                  <div 
                    className="inline-flex items-center justify-center w-16 h-16 bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-white/20 mb-4 group-hover:shadow-xl transition-all duration-300 group-hover:scale-110 cursor-pointer"
                    style={{ zIndex: 10 }}
                    onClick={() => {
                    const videoSection = document.querySelector('[data-section="video-demo"]');
                    if (videoSection) {
                      videoSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  <feature.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
